import type { CeremonyType } from '../api/types';

export const ceremonyLabels: Record<CeremonyType, string> = {
  customary: 'Cérémonie coutumière',
  civil: 'Cérémonie civile',
  religious: 'Cérémonie religieuse',
};

export function ceremonyLabel(value?: string) {
  if (!value) return 'Cérémonie';
  return ceremonyLabels[value as CeremonyType] || value;
}

export function formatDate(
  value?: string,
  timezone?: string,
  options?: Intl.DateTimeFormatOptions,
) {
  if (!value) return 'Date à préciser';
  try {
    return new Intl.DateTimeFormat('fr-FR', {
      dateStyle: 'long',
      timeStyle: 'short',
      timeZone: timezone || undefined,
      ...options,
    }).format(new Date(value));
  } catch {
    return value;
  }
}

export function formatShortDate(value?: string) {
  if (!value) return '—';
  try {
    return new Intl.DateTimeFormat('fr-FR', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    }).format(new Date(value));
  } catch {
    return value;
  }
}

/** Découpe une date de cérémonie pour l'afficher comme une vignette de calendrier. */
export function calendarParts(value?: string, timezone?: string) {
  const fallback = { weekday: '', day: '—', month: '', time: '' };
  if (!value) return fallback;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return fallback;
  const part = (options: Intl.DateTimeFormatOptions) => {
    try {
      return new Intl.DateTimeFormat('fr-FR', {
        timeZone: timezone || undefined,
        ...options,
      }).format(date);
    } catch {
      return '';
    }
  };
  return {
    weekday: part({ weekday: 'long' }),
    day: part({ day: '2-digit' }),
    month: part({ month: 'short' }),
    time: part({ hour: '2-digit', minute: '2-digit' }),
  };
}

export function statusLabel(status?: string) {
  const labels: Record<string, string> = {
    active: 'Actif',
    published: 'Publié',
    draft: 'Brouillon',
    suspended: 'Suspendu',
    archived: 'Archivé',
    revoked: 'Révoqué',
    disabled: 'Désactivé',
    confirmed: 'Confirmée',
    voided: 'Annulée',
    processing: 'Traitement',
    declined: 'Décliné',
    pending: 'En attente',
    none: 'Sans cérémonie',
  };
  return status ? labels[status] || status : 'Inconnu';
}

export function statusTone(status?: string): 'success' | 'danger' | 'warning' | 'neutral' {
  if (['active', 'published', 'confirmed', 'open'].includes(status || '')) return 'success';
  if (['revoked', 'archived', 'disabled', 'voided', 'declined'].includes(status || ''))
    return 'danger';
  if (['draft', 'suspended', 'processing', 'pending'].includes(status || '')) return 'warning';
  return 'neutral';
}

export function safeExternalUrl(value?: string) {
  if (!value) return undefined;
  try {
    const parsed = new URL(value);
    return parsed.protocol === 'https:' ? parsed.toString() : undefined;
  } catch {
    return undefined;
  }
}

export function makeIdempotencyKey() {
  return (
    globalThis.crypto?.randomUUID?.() || `${Date.now()}-${Math.random().toString(36).slice(2)}`
  );
}

export function initials(value?: string) {
  return (value || 'O R')
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase())
    .join('');
}
